import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Users, UserPlus, CheckSquare, ChevronRight, ChevronLeft, Plus, Trash2, Loader2, Upload, Download, X } from 'lucide-react';
import { teamService } from '../services/teamService';
import { AGE_GROUPS } from '../types';
import logoImage from '../assets/logo.svg';

const STEPS = [
  { id: 'welcome', label: 'Welcome', icon: Sparkles },
  { id: 'team', label: 'Create Team', icon: Users },
  { id: 'players', label: 'Add Players', icon: UserPlus },
  { id: 'ready', label: 'First Assessment', icon: CheckSquare },
];

const POSITIONS = ['Goalkeeper', 'Defender', 'Midfield', 'Winger', 'Forward'];

interface PlayerEntry { firstName: string; lastName: string; position: string }

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Team state
  const [teamName, setTeamName] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [teamId, setTeamId] = useState('');

  // Players state
  const [players, setPlayers] = useState<PlayerEntry[]>([{ firstName: '', lastName: '', position: '' }]);
  const [createdPlayers, setCreatedPlayers] = useState<{ id: string; firstName: string; lastName: string; position: string }[]>([]);

  const canAdvance = () => {
    if (step === 1) return teamName.trim().length >= 2 && ageGroup;
    if (step === 2) return players.some(p => p.firstName.trim() && p.lastName.trim() && p.position);
    return true;
  };

  // Player selection state (step 4)
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [showCsvImport, setShowCsvImport] = useState(false);

  const handleCreateTeam = async () => {
    setSaving(true); setError('');
    try {
      const team = await teamService.createTeam({ name: teamName.trim(), ageGroup, season: '2025/26' });
      setTeamId(team.id);
      setStep(2);
      window.scrollTo(0, 0);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create team');
    } finally { setSaving(false); }
  };

  const handleCreatePlayers = async () => {
    if (!teamId) return;
    setSaving(true); setError('');
    try {
      const valid = players.filter(p => p.firstName.trim() && p.lastName.trim() && p.position);
      const created: { id: string; firstName: string; lastName: string; position: string }[] = [];
      for (const p of valid) {
        const player = await teamService.createPlayer(teamId, {
          firstName: p.firstName.trim(), lastName: p.lastName.trim(),
          dateOfBirth: '2012-01-01', position: p.position,
        });
        created.push({ id: player.id, firstName: player.firstName, lastName: player.lastName, position: player.position });
      }
      setCreatedPlayers(created);
      setStep(3);
      window.scrollTo(0, 0);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to add players');
    } finally { setSaving(false); }
  };

  const handleStartAssessment = () => {
    if (!selectedPlayerId) return;
    localStorage.setItem('gamepath_onboarding', JSON.stringify({
      teamId, teamName, players: createdPlayers,
    }));
    navigate(`/onboarding/assess/${selectedPlayerId}`);
  };

  const addPlayer = () => setPlayers(prev => [...prev, { firstName: '', lastName: '', position: '' }]);
  const removePlayer = (i: number) => setPlayers(prev => prev.filter((_, idx) => idx !== i));
  const updatePlayer = (i: number, field: keyof PlayerEntry, value: string) => {
    setPlayers(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="text-center pt-8 pb-4">
        <img src={logoImage} alt="GamePath" className="h-8 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Let's get you set up in 3 quick steps</p>
      </div>

      {/* Step Indicator */}
      <div className="max-w-lg mx-auto w-full px-4 mb-6">
        <div className="flex items-center justify-between relative">
          {/* Progress bar background */}
          <div className="absolute top-5 left-8 right-8 h-1 bg-gray-200 rounded-full" />
          <div className="absolute top-5 left-8 h-1 bg-green-600 rounded-full transition-all duration-500"
            style={{ width: `${(step / (STEPS.length - 1)) * (100 - 12)}%` }} />
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.id} className="flex flex-col items-center relative z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  done ? 'bg-green-600 text-white' : active ? 'bg-green-600 text-white ring-4 ring-green-100' : 'bg-gray-200 text-gray-400'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`text-xs mt-1.5 font-medium ${active || done ? 'text-green-700' : 'text-gray-400'}`}>{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Card */}
      <div className="max-w-lg mx-auto w-full px-4 flex-1">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 lg:p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to GamePath!</h2>
              <p className="text-gray-500 mb-6">
                GamePath helps you track player development through structured assessments. Let's set up your first team and get started.
              </p>
              <div className="bg-gray-50 rounded-xl p-5 text-left">
                <p className="font-semibold text-gray-900 mb-3">What you'll do:</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><span className="text-green-600">•</span> Create your team</li>
                  <li className="flex items-center gap-2"><span className="text-green-600">•</span> Add your players</li>
                  <li className="flex items-center gap-2"><span className="text-green-600">•</span> Complete your first assessment</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 1: Create Team */}
          {step === 1 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Create Your Team</h2>
                <p className="text-gray-500 text-sm">Just the basics to get started</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Team Name <span className="text-red-500">*</span></label>
                  <input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="e.g. Riverside U14s"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Age Group <span className="text-red-500">*</span></label>
                  <select value={ageGroup} onChange={e => setAgeGroup(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="">Select age group</option>
                    {AGE_GROUPS.map(ag => <option key={ag} value={ag}>{ag}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Add Players */}
          {step === 2 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Add Your Players</h2>
                <p className="text-gray-500 text-sm">Add at least one player to get started</p>
              </div>
              <div className="space-y-3">
                {players.map((p, i) => (
                  <div key={i} className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex gap-2">
                      <input value={p.firstName} onChange={e => updatePlayer(i, 'firstName', e.target.value)} placeholder="First name"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                      <input value={p.lastName} onChange={e => updatePlayer(i, 'lastName', e.target.value)} placeholder="Last name"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                    </div>
                    <div className="flex gap-2 items-center">
                      <select value={p.position} onChange={e => updatePlayer(i, 'position', e.target.value)}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
                        <option value="">Select position</option>
                        {POSITIONS.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                      </select>
                      {players.length > 1 && (
                        <button onClick={() => removePlayer(i)} className="p-2 text-gray-400 hover:text-red-500 min-h-[44px] min-w-[44px] flex items-center justify-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={addPlayer} className="mt-3 w-full border-2 border-dashed border-gray-300 rounded-lg py-3 text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                <Plus className="w-4 h-4" /> Add Another Player
              </button>
              <p className="text-xs text-gray-400 text-center mt-2">You can add more players later from your dashboard.</p>

              {/* Advanced CSV upload */}
              <button onClick={() => setShowCsvImport(true)}
                className="mt-3 w-full border border-gray-200 rounded-lg py-3 text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                <Upload className="w-4 h-4" /> Advanced: Upload via CSV
              </button>

              {showCsvImport && teamId && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowCsvImport(false); }}>
                  <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
                    <div className="flex items-center justify-between p-6 pb-4">
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">Upload Players via CSV</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Add multiple players at once using a CSV file</p>
                      </div>
                      <button onClick={() => setShowCsvImport(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="px-6 pb-6 space-y-4">
                      {/* Step 1: Download Template */}
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="flex items-start gap-3">
                          <Download className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">Step 1: Download Template</p>
                            <p className="text-xs text-gray-500 mt-0.5 mb-3">Get our CSV template with the correct format.</p>
                            <button onClick={() => {
                              const csv = 'firstName,lastName,dateOfBirth,position\nJohn,Smith,2010-05-15,Forward\nEmma,Johnson,2011-03-22,Midfield';
                              const blob = new Blob([csv], { type: 'text/csv' });
                              const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                              a.download = 'player-import-template.csv'; a.click();
                            }} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
                              <Download className="w-4 h-4" /> Download Template
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Step 2: Upload File */}
                      <div className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-300 text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="font-semibold text-gray-900 text-sm">Step 2: Upload Your File</p>
                        <p className="text-xs text-gray-500 mt-0.5 mb-3">Select your completed CSV file</p>
                        <label className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px] cursor-pointer">
                          <Upload className="w-4 h-4" /> Choose CSV File
                          <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const text = await file.text();
                            setError('');
                            setSaving(true);
                            try {
                              const imported = await teamService.importPlayers(teamId, text);
                              const newCreated = imported.map(p => ({ id: p.id, firstName: p.firstName, lastName: p.lastName, position: p.position }));
                              setCreatedPlayers(prev => [...prev, ...newCreated]);
                              setShowCsvImport(false);
                              setStep(3);
                              window.scrollTo(0, 0);
                            } catch (err: any) {
                              setError(err.response?.data?.error?.message || 'Failed to import players');
                            } finally { setSaving(false); }
                          }} />
                        </label>
                      </div>

                      {/* Format hint */}
                      <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                        <p className="text-xs text-green-800">
                          <span className="font-semibold">CSV Format:</span> First Name, Last Name, Date of Birth, Position
                        </p>
                        <p className="text-xs text-green-700 mt-0.5">
                          Accepted positions: Goalkeeper, Defender, Midfield, Winger, Forward
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Ready */}
          {step === 3 && (
            <div className="text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckSquare className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">You're All Set!</h2>
              <p className="text-gray-500 mb-6">
                Great! You've created <span className="font-semibold text-gray-900">{teamName}</span> with <span className="font-semibold text-gray-900">{createdPlayers.length} player{createdPlayers.length !== 1 ? 's' : ''}</span>.
              </p>
              <div className="bg-gray-50 rounded-xl p-5 text-left mb-4">
                <p className="font-semibold text-gray-900 mb-3 text-center">Next Step</p>
                <p className="text-sm text-gray-600 text-center mb-4">
                  Let's complete your first player assessment. This will establish a baseline to track development over time.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><span className="text-green-600">•</span> Takes about 5-10 minutes</li>
                  <li className="flex items-center gap-2"><span className="text-green-600">•</span> Rate skills from 1-10</li>
                  <li className="flex items-center gap-2"><span className="text-green-600">•</span> Covers Technical, Physical, Psychological & Social</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 4: Select Player */}
          {step === 4 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Select Player for First Assessment</h2>
                <p className="text-gray-500 text-sm">Choose which player you'd like to assess first</p>
              </div>
              <div className="space-y-2">
                {createdPlayers.map(p => (
                  <button key={p.id} onClick={() => setSelectedPlayerId(p.id)}
                    className={`w-full text-left p-4 rounded-lg border transition-colors min-h-[44px] ${
                      selectedPlayerId === p.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <p className="font-medium text-gray-900">{p.firstName} {p.lastName}</p>
                    <p className="text-sm text-gray-500">{p.position}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6 mb-8">
          <div>
            {step > 0 && step < 3 && (
              <button onClick={() => { setStep(s => s - 1); setError(''); window.scrollTo(0, 0); }}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800 min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>
          <div>
            {step === 0 && (
              <button onClick={() => { setStep(1); window.scrollTo(0, 0); }}
                className="inline-flex items-center gap-1 px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px] w-full justify-center">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {step === 1 && (
              <button onClick={handleCreateTeam} disabled={!canAdvance() || saving}
                className="inline-flex items-center gap-1 px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {saving ? 'Creating...' : 'Continue'} {!saving && <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            {step === 2 && (
              <div className="flex flex-col items-end gap-2">
                <button onClick={handleCreatePlayers} disabled={!canAdvance() || saving}
                  className="inline-flex items-center gap-1 px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {saving ? 'Adding...' : 'Continue'} {!saving && <ChevronRight className="w-4 h-4" />}
                </button>
                <button onClick={() => { setStep(3); window.scrollTo(0, 0); }}
                  className="text-xs text-gray-400 hover:text-gray-600 min-h-[44px]">
                  I'll add players later
                </button>
              </div>
            )}
            {step === 3 && (
              <button onClick={() => { setStep(4); window.scrollTo(0, 0); }}
                className="inline-flex items-center gap-1 px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
                Start First Assessment <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {step === 4 && (
              <button onClick={handleStartAssessment} disabled={!selectedPlayerId}
                className="inline-flex items-center gap-1 px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed">
                Start Assessment <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
